import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { Gridster, GridsterConfig, GridsterItem } from 'angular-gridster2';
import { isEqual } from 'lodash';
import { catchError, defer, filter, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { DisplayType, FieldOrigin, FieldTypeEnum } from '@models/module/fields/enums';
import { FieldModel, ScreenFieldModel } from '@models/module/fields/main';
import { FieldCardComponent, fieldCardBackground } from '@module-definition/form/field-card';
import { ScreenModel } from '@module-definition/screen/common';
import { FieldTypeIndicatorComponent } from '@shared/components/field-type-indicator';
import { LoadErrorComponent } from '@shared/components/load-error/load-error.component';
import { ConfirmationDialog } from '@shared/dialogs';
import { NotificationService, UserService } from '@shared/services';

import { ModuleConfigDictionariesService, ModuleConfigPanel, provideModuleConfigPanel } from '../common';
import { ModuleConfigDesignerSkeletonComponent } from '../common/skeletons/designer-skeleton.component';
import { ModuleConfigListSkeletonComponent } from '../common/skeletons/list-skeleton.component';
import { AnnotationDialogComponent, AnnotationDialogData } from './annotation-dialog/annotation-dialog.component';
import {
  DesignerItem,
  DesignerLayoutEntry,
  DisplayTypeDefinition,
  FieldDefinitionsService,
  FieldTypeDefinition,
  ScreenFieldsService,
  addableFieldTypes,
  designerUid,
  gridDefaults,
  isKnownFieldType,
  layoutOf,
} from './common';
import { FieldConfigDialogComponent } from './field-config-dialog/field-config-dialog.component';
import { FieldConfigDialogData, FieldConfigDialogResult } from './field-config-dialog/field-config-dialog.models';

const GRID_OPTIONS: GridsterConfig = {
  gridType: 'verticalFixed',
  compactType: 'none',
  margin: 8,
  outerMargin: true,
  mobileBreakpoint: 300,
  minCols: 12,
  maxCols: 12,
  minRows: 10,
  maxRows: 120,
  maxItemCols: 20,
  minItemCols: 1,
  maxItemRows: 3,
  minItemRows: 1,
  maxItemArea: 2500,
  minItemArea: 1,
  defaultItemCols: 1,
  defaultItemRows: 1,
  fixedRowHeight: 64,
  enableEmptyCellClick: false,
  enableEmptyCellContextMenu: false,
  enableEmptyCellDrop: false,
  enableEmptyCellDrag: false,
  swap: true,
  pushItems: true,
  disablePushOnDrag: false,
  disablePushOnResize: false,
  pushDirections: { north: true, east: true, south: true, west: true },
  pushResizeItems: false,
  displayGrid: 'always',
  disableWindowResize: false,
  disableWarnings: true,
  scrollToNewItems: true,
};

const ANNOTATION_FIELD: Partial<ScreenFieldModel> = {
  typeId: FieldTypeEnum.Annotation,
  displayTypeId: DisplayType.Text,
  name: 'annotation',
  label: 'annotation',
};

@Component({
  selector: 'cipo-module-screen-designer',
  imports: [
    FieldCardComponent,
    FieldTypeIndicatorComponent,
    FlexLayoutModule,
    Gridster,
    GridsterItem,
    LoadErrorComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    ModuleConfigDesignerSkeletonComponent,
    ModuleConfigListSkeletonComponent,
    TranslateModule,
  ],
  templateUrl: './screen-designer.component.html',
  styleUrl: './screen-designer.component.scss',
  providers: [provideModuleConfigPanel(() => ScreenDesignerComponent)],
  host: { class: 'cipo--display--block cipo--height--full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreenDesignerComponent implements ModuleConfigPanel {
  readonly fieldCardBackground = fieldCardBackground;

  private readonly screenFields = inject(ScreenFieldsService);
  private readonly fieldDefinitions = inject(FieldDefinitionsService);
  private readonly dictionaries = inject(ModuleConfigDictionariesService);
  private readonly dialog = inject(MatDialog);
  private readonly notify = inject(NotificationService);
  private readonly userService = inject(UserService);

  readonly moduleId = input.required<number>();
  readonly moduleTypeId = input.required<number>();
  readonly screen = input.required<ScreenModel>();
  readonly perContract = input(false);
  readonly canUpdate = input(false);

  readonly loading = signal(false);
  readonly failed = signal(false);
  readonly saving = signal(false);
  readonly loadingFields = signal(false);

  readonly items = signal<DesignerItem[]>([]);
  readonly fields = signal<FieldModel[]>([]);
  readonly showContractFields = signal(false);
  readonly fieldQuery = signal('');

  private readonly entityId = signal<number>(null);
  private readonly savedLayout = signal<DesignerLayoutEntry[]>([]);
  private readonly layoutVersion = signal(0);
  private readonly removedItems = new Map<string, DesignerItem>();

  readonly isBusy = computed(() => this.loading() || this.saving() || this.loadingFields());
  readonly savable = computed(() => this.canUpdate());

  readonly isDirty = computed(() => {
    this.layoutVersion();
    return !isEqual(layoutOf(this.items()), this.savedLayout());
  });

  readonly gridOptions = computed<GridsterConfig>(() => ({
    ...GRID_OPTIONS,
    draggable: { delayStart: 0, enabled: this.canUpdate() },
    resizable: { delayStart: 0, enabled: this.canUpdate() },
    itemChangeCallback: () => this.layoutVersion.update(version => version + 1),
  }));

  readonly addableTypes = computed(() => addableFieldTypes(this.moduleId(), this.perContract()));

  // Screen fields omit multiplicity; use the field definition without replacing screen-specific restrictions.
  readonly itemTypeFields = computed(() => {
    const definitions = new Map(this.fields().map(field => [field.id, field]));
    return new Map(
      this.items().map(item => [
        item.uid,
        { ...item.field, hasMultipleValues: definitions.get(item.field.id)?.hasMultipleValues },
      ]),
    );
  });

  private readonly onFormIds = computed(
    () =>
      new Set(
        this.items()
          .filter(item => !this.isAnnotation(item))
          .map(item => item.field.id),
      ),
  );

  private readonly savedUids = computed(
    () => new Map(this.savedLayout().map(entry => [entry.uid, entry.annotation] as [string, string])),
  );

  readonly visibleFields = computed(() => {
    const query = this.fieldQuery().trim().toLowerCase();
    const fields = this.fields();
    return query
      ? fields.filter(field => `${field.name ?? ''} ${field.label ?? ''}`.toLowerCase().includes(query))
      : fields;
  });

  constructor() {
    effect(() => {
      const moduleId = this.moduleId();
      const screen = this.screen();
      if (moduleId && screen?.id) {
        untracked(() => this.load());
      }
    });
  }

  load({ preserveContractFields = false }: { preserveContractFields?: boolean } = {}): void {
    const moduleId = this.moduleId();
    const screenId = this.screen().id;

    this.loading.set(true);
    this.failed.set(false);

    forkJoin({
      // The API infers this filter from placed fields; saving must preserve the user's selection.
      hasContractFields: preserveContractFields
        ? of(this.showContractFields())
        : this.screenFields.hasContractFields(moduleId, screenId).pipe(catchError(() => of(false))),
      entities: this.dictionaries.getModuleEntities(moduleId, this.moduleTypeId()),
    })
      .pipe(
        switchMap(({ hasContractFields, entities }) => {
          this.showContractFields.set(hasContractFields);
          this.entityId.set(entities[0]?.key ?? null);
          return forkJoin({
            screenFields: this.screenFields.search(moduleId, screenId),
            fields: this.searchFields(),
          });
        }),
      )
      .subscribe({
        next: ({ screenFields, fields }) => {
          this.applyScreenFields(screenFields);
          this.fields.set(fields);
          this.loading.set(false);
        },
        error: () => {
          this.failed.set(true);
          this.loading.set(false);
        },
      });
  }

  // MARK: template helpers

  isAnnotation(item: DesignerItem): boolean {
    return item.field.typeId === FieldTypeEnum.Annotation;
  }

  isOnForm(field: FieldModel): boolean {
    return this.onFormIds().has(field.id);
  }

  isFieldUnsaved(field: FieldModel): boolean {
    return this.isOnForm(field) !== !!field.inUse;
  }

  canDeleteField(field: FieldModel): boolean {
    return !field.isContractSystemField && !field.isSystemField && !field.relationId;
  }

  isItemUnsaved(item: DesignerItem): boolean {
    const saved = this.savedUids();
    return !saved.has(item.uid) || saved.get(item.uid) !== item.field.annotation;
  }

  // MARK: fields list

  toggleContractFields(show: boolean): void {
    this.showContractFields.set(show);
    this.reloadFields();
  }

  addToForm(field: FieldModel): void {
    if (!field.displayTypeId) {
      this.notify.warning('modules.screenDesigner.noDisplayType');
      this.openFieldDialog({ fieldId: field.id, fieldOrigin: field.fieldOrigin, addToForm: true });
      return;
    }
    const screenField = field as unknown as ScreenFieldModel;
    const removed = this.removedItems.get(designerUid(screenField, 0));
    if (removed) {
      this.removedItems.delete(removed.uid);
      this.items.update(items => [...items, removed]);
      return;
    }
    this.pushItem({ ...screenField });
  }

  removeFromForm(field: FieldModel): void {
    const item = this.items().find(entry => !this.isAnnotation(entry) && entry.field.id === field.id);
    if (item) {
      this.removeItem(item);
    }
  }

  editField(field: FieldModel): void {
    this.openFieldDialog({ fieldId: field.id, fieldOrigin: field.fieldOrigin });
  }

  deleteField(field: FieldModel): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'modules.screenDesigner.deleteFieldTitle',
          description: 'modules.screenDesigner.deleteFieldMessage',
          params: { name: field.name },
        },
      })
      .afterClosed()
      .pipe(filter(confirmed => !!confirmed))
      .subscribe(() => {
        this.saving.set(true);
        this.fieldDefinitions.delete(this.moduleId(), field.id).subscribe({
          next: () => {
            this.notify.success('modules.screenDesigner.fieldDeletedSuccess');
            this.saving.set(false);
            this.invalidateFieldDictionaries();
            this.reloadFields();
          },
          error: () => this.saving.set(false),
        });
      });
  }

  createField(fieldType: FieldTypeDefinition, displayType: DisplayTypeDefinition): void {
    const screen = this.screen();
    this.openFieldDialog({
      newField: { typeId: fieldType.typeId, displayTypeId: displayType.displayTypeId },
      fieldOrigin: screen.isStartScreen ? FieldOrigin.Property : FieldOrigin.Local,
      screenId: screen.isStartScreen ? undefined : screen.id,
      addToForm: true,
    });
  }

  // MARK: grid

  editItem(item: DesignerItem): void {
    if (this.isAnnotation(item)) {
      this.openAnnotationDialog(item);
      return;
    }
    this.openFieldDialog({ fieldId: item.field.id, fieldOrigin: item.field.fieldOrigin, screenId: this.screen().id });
  }

  removeItem(item: DesignerItem): void {
    if (!this.isAnnotation(item)) {
      this.removedItems.set(item.uid, item);
    }
    this.items.update(items => items.filter(entry => entry !== item));
  }

  addAnnotation(): void {
    this.openAnnotationDialog();
  }

  // MARK: save

  save(): Observable<boolean> {
    return defer(() => {
      if (this.isBusy() || !this.isDirty()) {
        return of(false);
      }
      const body = this.items().map(({ field, x, y, cols, rows }) => ({ ...field, x, y, cols, rows }));

      this.saving.set(true);
      return this.screenFields.syncToScreen(this.moduleId(), this.screen().id, body).pipe(
        map(() => {
          this.notify.success('modules.screenDesigner.savedSuccess');
          this.saving.set(false);
          this.invalidateFieldDictionaries();
          this.load({ preserveContractFields: true });
          return true;
        }),
        catchError(() => {
          this.saving.set(false);
          return of(false);
        }),
      );
    });
  }

  // MARK: private

  private searchFields() {
    return this.fieldDefinitions.search(this.moduleId(), {
      screenId: this.screen().id,
      entityId: this.entityId(),
      addContractFields: this.showContractFields(),
    });
  }

  private reloadFields(addToFormId?: number): void {
    this.loadingFields.set(true);
    this.searchFields().subscribe({
      next: fields => {
        this.fields.set(fields);
        this.loadingFields.set(false);
        const added = addToFormId ? fields.find(field => field.id === addToFormId) : undefined;
        if (added && !this.isOnForm(added)) {
          this.addToForm(added);
        }
      },
      error: () => this.loadingFields.set(false),
    });
  }

  private applyScreenFields(screenFields: ScreenFieldModel[]): void {
    const items = screenFields.map((field, index) => this.toItem(field, index));
    this.removedItems.clear();
    this.items.set(items);
    this.savedLayout.set(layoutOf(items));
  }

  private toItem(field: ScreenFieldModel, index: number, placeAutomatically = false): DesignerItem {
    const defaults = gridDefaults(field);
    const maxRows = defaults?.maxItemRows;
    const rows = field.rows || defaults?.rows || 1;
    return {
      uid: designerUid(field, index),
      field,
      x: placeAutomatically ? -1 : (field.x ?? 0),
      y: placeAutomatically ? -1 : (field.y ?? 0),
      cols: field.cols || defaults?.cols || 1,
      rows: maxRows && rows > maxRows ? maxRows : rows,
      minItemCols: defaults?.minItemCols,
      minItemRows: defaults?.minItemRows,
      maxItemRows: defaults?.maxItemRows,
      maxItemCols: defaults?.maxItemCols,
      unknownType: !isKnownFieldType(field.typeId),
    };
  }

  private pushItem(field: ScreenFieldModel): void {
    const index = Date.now();
    this.items.update(items => [...items, this.toItem({ ...field, x: undefined, y: undefined }, index, true)]);
  }

  private openAnnotationDialog(item?: DesignerItem): void {
    this.dialog
      .open(AnnotationDialogComponent, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().md,
        data: { annotation: item?.field.annotation } satisfies AnnotationDialogData,
      })
      .afterClosed()
      .pipe(filter((annotation): annotation is string => !!annotation))
      .subscribe(annotation => {
        if (item) {
          item.field = { ...item.field, annotation };
          this.items.update(items => [...items]);
          return;
        }
        this.pushItem({ ...ANNOTATION_FIELD, annotation } as ScreenFieldModel);
      });
  }

  private openFieldDialog(options: Partial<FieldConfigDialogData>): void {
    this.dialog
      .open(FieldConfigDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().lg,
        data: {
          moduleId: this.moduleId(),
          moduleTypeId: this.moduleTypeId(),
          entityId: this.entityId(),
          canAddToForm: true,
          ...options,
        } satisfies FieldConfigDialogData,
      })
      .afterClosed()
      .pipe(filter((result): result is FieldConfigDialogResult => !!result?.field))
      .subscribe(result => this.afterFieldSaved(result));
  }

  private invalidateFieldDictionaries(): void {
    ['conditionalFields', 'moduleFields'].forEach(prefix => this.dictionaries.invalidate(prefix));
  }

  private afterFieldSaved({ field, screenId, addToForm }: FieldConfigDialogResult): void {
    this.invalidateFieldDictionaries();
    const item = this.items().find(entry => !this.isAnnotation(entry) && entry.field.id === field.id);
    if (screenId === this.screen().id && item) {
      const { x, y, cols, rows } = item;
      const merged = this.toItem({ ...item.field, ...field, x, y, cols, rows } as ScreenFieldModel, 0);
      Object.assign(item, { ...merged, uid: item.uid, x, y, cols, rows: merged.rows });
      this.items.update(items => [...items]);
      return;
    }
    this.reloadFields(addToForm ? field.id : undefined);
  }
}
